import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle, AlertCircle, Plane } from 'lucide-react';

const CampRegistrationForm = () => {
  const { campId } = useParams();
  
  // מיפוי של campId לשם היעד - חייב להתאים לערכים ב-Select
  const campNames = {
    'belgrade': 'בלגרד, סרביה',
    'dubai': 'דובאי, איחוד האמירויות',
    'montenegro': 'מונטנגרו'
  };
  
  const [formData, setFormData] = useState({
    fullNameHebrew: '',
    fullNameEnglish: '',
    destination: campId && campNames[campId] ? campNames[campId] : '',
    roomType: '',
    phone: '',
    email: '',
    age: '',
    paymentMethod: 'מזומן',
    healthDeclaration: false,
    legalDeclaration: false,
    signature: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.fullNameHebrew) return 'נא למלא שם מלא בעברית';
    if (!formData.fullNameEnglish) return 'נא למלא שם מלא באנגלית';
    if (!formData.destination) return 'נא לבחור יעד';
    if (!formData.roomType) return 'נא לבחור סוג חדר';
    if (!formData.phone) return 'נא למלא מספר טלפון';
    if (!formData.email) return 'נא למלא כתובת אימייל';
    if (!formData.age) return 'נא למלא גיל';
    if (!formData.healthDeclaration) return 'נא לאשר הצהרת בריאות';
    if (!formData.legalDeclaration) return 'נא לאשר הצהרה משפטית';
    if (!formData.signature) return 'נא להזין חתימה';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // הכנת ההודעה לווצאפ
      const message = `🎾 הרשמה חדשה למחנה אימונים 🎾

📝 פרטים אישיים:
שם מלא (עברית): ${formData.fullNameHebrew}
שם מלא (אנגלית): ${formData.fullNameEnglish}
גיל: ${formData.age}
טלפון: ${formData.phone}
אימייל: ${formData.email}

✈️ פרטי המחנה:
יעד: ${formData.destination}
סוג חדר: ${formData.roomType}
אופן תשלום: ${formData.paymentMethod}

✅ הצהרות:
הצהרת בריאות: מאושר
הצהרה משפטית: מאושר
חתימה: ${formData.signature}

תאריך הרשמה: ${new Date().toLocaleString('he-IL')}`;

      // שליחה לווצאפ
      const whatsappUrl = `https://wa.me/972544822788?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');

      setSubmitted(true);
    } catch (err) {
      setError('אירעה שגיאה בשליחת הטופס. אנא נסה שוב.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="border-green-200 shadow-2xl">
            <CardContent className="p-8 text-center">
              <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-gray-900 mb-4">הטופס נשלח בהצלחה!</h2>
              <p className="text-lg text-gray-700 mb-6">
                פרטי ההרשמה שלך נשלחו אלינו בוואטסאפ.
                <br />
                נחזור אליך בהקדם!
              </p>
              <Button 
                onClick={() => {
                  setSubmitted(false);
                  setFormData({
                    fullNameHebrew: '',
                    fullNameEnglish: '',
                    destination: '',
                    roomType: '',
                    phone: '',
                    email: '',
                    age: '',
                    paymentMethod: 'מזומן',
                    healthDeclaration: false,
                    legalDeclaration: false,
                    signature: ''
                  });
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                הרשמה נוספת
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Plane className="w-12 h-12 text-blue-600" />
            <h1 className="text-4xl md:text-5xl font-black text-gray-900">
              טופס הרשמה למחנה אימונים
            </h1>
            <Plane className="w-12 h-12 text-blue-600" style={{ transform: 'scaleX(-1)' }} />
          </div>
          <p className="text-xl text-gray-600">
            מלא את הפרטים והצטרף למחנה הבא!
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <Card className="shadow-2xl">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
              <CardTitle className="text-2xl">פרטים אישיים ופרטי מחנה</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              
              {error && (
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-red-700 font-medium">{error}</p>
                </div>
              )}

              {/* שם מלא בעברית */}
              <div>
                <Label htmlFor="fullNameHebrew" className="text-lg font-bold">שם מלא בעברית *</Label>
                <Input
                  id="fullNameHebrew"
                  value={formData.fullNameHebrew}
                  onChange={(e) => handleChange('fullNameHebrew', e.target.value)}
                  placeholder="לדוגמה: יוסי כהן"
                  className="mt-2 text-lg"
                  required
                />
              </div>

              {/* שם מלא באנגלית */}
              <div>
                <Label htmlFor="fullNameEnglish" className="text-lg font-bold">שם מלא באנגלית (כמו בדרכון) *</Label>
                <Input
                  id="fullNameEnglish"
                  value={formData.fullNameEnglish}
                  onChange={(e) => handleChange('fullNameEnglish', e.target.value)}
                  placeholder="Example: Yossi Cohen"
                  className="mt-2 text-lg"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* גיל */}
                <div>
                  <Label htmlFor="age" className="text-lg font-bold">גיל *</Label>
                  <Input
                    id="age"
                    type="number"
                    value={formData.age}
                    onChange={(e) => handleChange('age', e.target.value)}
                    placeholder="25"
                    className="mt-2 text-lg"
                    min="1"
                    max="120"
                    required
                  />
                </div>

                {/* טלפון */}
                <div>
                  <Label htmlFor="phone" className="text-lg font-bold">טלפון *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="050-1234567"
                    className="mt-2 text-lg"
                    required
                  />
                </div>
              </div>

              {/* אימייל */}
              <div>
                <Label htmlFor="email" className="text-lg font-bold">כתובת אימייל *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="example@email.com"
                  className="mt-2 text-lg"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* יעד */}
                <div>
                  <Label htmlFor="destination" className="text-lg font-bold">בחר יעד *</Label>
                  <Select value={formData.destination} onValueChange={(value) => handleChange('destination', value)}>
                    <SelectTrigger className="mt-2 text-lg">
                      <SelectValue placeholder="בחר יעד..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="בלגרד, סרביה">🏛️ בלגרד, סרביה</SelectItem>
                      <SelectItem value="דובאי, איחוד האמירויות">🏜️ דובאי, איחוד האמירויות</SelectItem>
                      <SelectItem value="מונטנגרו">⛰️ מונטנגרו</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* סוג חדר */}
                <div>
                  <Label htmlFor="roomType" className="text-lg font-bold">סוג חדר *</Label>
                  <Select value={formData.roomType} onValueChange={(value) => handleChange('roomType', value)}>
                    <SelectTrigger className="mt-2 text-lg">
                      <SelectValue placeholder="בחר סוג חדר..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="חדר יחיד">🛏️ חדר יחיד</SelectItem>
                      <SelectItem value="חדר זוגי">🛏️🛏️ חדר זוגי</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* אופן תשלום */}
              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
                <Label className="text-lg font-bold">אופן תשלום</Label>
                <p className="text-lg text-gray-700 mt-2">💵 מזומן בלבד</p>
              </div>

              {/* הצהרת בריאות */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="healthDeclaration"
                    checked={formData.healthDeclaration}
                    onCheckedChange={(checked) => handleChange('healthDeclaration', checked)}
                    className="mt-1"
                  />
                  <Label htmlFor="healthDeclaration" className="text-base cursor-pointer">
                    <span className="font-bold">הצהרת בריאות *</span>
                    <p className="mt-1 text-gray-700">
                      אני מצהיר/ה כי אני בריא/ה ומסוגל/ת להשתתף באימוני טניס אינטנסיביים. 
                      אין לי מגבלות רפואיות המונעות ממני להשתתף במחנה האימונים.
                    </p>
                  </Label>
                </div>
              </div>

              {/* הצהרה משפטית */}
              <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="legalDeclaration"
                    checked={formData.legalDeclaration}
                    onCheckedChange={(checked) => handleChange('legalDeclaration', checked)}
                    className="mt-1"
                  />
                  <Label htmlFor="legalDeclaration" className="text-base cursor-pointer">
                    <span className="font-bold">הצהרה משפטית *</span>
                    <p className="mt-1 text-gray-700">
                      אני מצהיר/ה כי קראתי והבנתי את תנאי ההשתתפות במחנה האימונים. 
                      אני משחרר/ת את המארגנים מכל אחריות במקרה של פציעה או תאונה.
                      אני מתחייב/ת לפעול לפי הוראות המאמנים והצוות המלווה.
                    </p>
                  </Label>
                </div>
              </div>

              {/* חתימה */}
              <div>
                <Label htmlFor="signature" className="text-lg font-bold">חתימה דיגיטלית *</Label>
                <p className="text-sm text-gray-600 mt-1">הקלד את שמך המלא כחתימה</p>
                <Input
                  id="signature"
                  value={formData.signature}
                  onChange={(e) => handleChange('signature', e.target.value)}
                  placeholder="השם המלא שלך"
                  className="mt-2 text-lg font-['Brush_Script_MT',cursive]"
                  required
                />
              </div>

              {/* כפתור שליחה */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-6 text-xl font-bold"
              >
                {loading ? (
                  <span>שולח...</span>
                ) : (
                  <span>שלח הרשמה לווצאפ 📱</span>
                )}
              </Button>

              <p className="text-center text-sm text-gray-600">
                לאחר שליחת הטופס, תפתח חלון וואטסאפ עם פרטי ההרשמה שלך
              </p>
            </CardContent>
          </Card>
        </form>
      </div>
    </div>
  );
};

export default CampRegistrationForm;
